import { useId } from 'react';
import { Link } from '@tanstack/react-router';
import { MonitorIcon, MoonIcon, SettingsIcon, SunIcon } from 'lucide-react';

import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { ADMIN_ENTRY, visibleNavItems } from '@/features/app-shell';
import { useIsOrgAdmin } from '@/features/identity';
import { clearPersonalDrafts } from '@/shared/store/personalDrafts';
import { useSession } from '@/lib/auth/session';
import { displayNameOf } from './accountIdentity';
import { THEME_OPTIONS, useThemeStore, type Theme } from '@/shared/store/theme';
import { useShortcutSheetStore } from '@/shared/store/shortcutSheet';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

const THEME_ICONS = {
  light: SunIcon,
  system: MonitorIcon,
  dark: MoonIcon,
} as const satisfies Record<Theme, unknown>;

/**
 * The body of the account menu, wherever it hangs from.
 *
 * Shared between the sidebar's owner row and the front door's account chip so
 * the two cannot offer different things — the sidebar was the only route to
 * signing out for as long as it was the only place this existed.
 *
 * Theme is here, and only here since spec 128: it is a choice for this
 * device, reachable from every page, so a workspace's Settings no longer
 * repeats it. The store persists it, never the service.
 */
export function AccountMenuItems({
  showIdentity = false,
}: {
  /**
   * Repeat the person's name and address above the actions. False where the
   * menu already hangs off a row that names them, which would make this a
   * duplicate rather than a heading.
   */
  showIdentity?: boolean;
}) {
  const { user, signOut, switchAccount } = useSession();
  const theme = useThemeStore((state) => state.theme);
  const openShortcuts = useShortcutSheetStore((state) => state.setOpen);
  // A phone has no keyboard to take shortcuts from (spec 261).
  const phone = usePhoneWidth();
  const setTheme = useThemeStore((state) => state.setTheme);
  const themeLabelId = useId();
  /*
    The organization console's entry point (spec 073 FR-003).

    Here rather than in the workspace sidebar because the console sits above the
    workspace boundary — it is not one of a workspace's destinations, and putting it in
    a sidebar that names a workspace would say it was. This menu is the one navigation
    the product has in both places a person can be: inside a workspace, and at the front
    door looking at the list of them.

    Filtered through `visibleNavItems` rather than an `isOrgAdmin &&` here, so the two
    conditional surfaces in the product are gated by one mechanism.
  */
  const isOrgAdmin = useIsOrgAdmin();
  const [adminEntry] = visibleNavItems([ADMIN_ENTRY], {
    isOrgAdmin,
    isWorkspaceOwner: false,
  });

  return (
    <>
      {showIdentity && user && (
        <>
          <DropdownMenuGroup>
            <DropdownMenuLabel className="flex flex-col gap-0.5">
              <span className="text-foreground truncate text-sm font-medium">
                {displayNameOf(user.displayName, user.email)}
              </span>
              {/* The address is the line that actually identifies someone: two
                  colleagues can share a display name and cannot share an
                  address. Trimmed, so a whitespace-only name does not render a
                  blank heading with the address repeated beneath it. */}
              {user.displayName.trim() && (
                <span className="text-muted-foreground truncate text-xs font-normal">
                  {user.email}
                </span>
              )}
            </DropdownMenuLabel>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
        </>
      )}

      {/* One way into the person's settings (spec 239): the settings area's
          rail lists its pages, so a new page changes the rail and never this
          menu. A link, so it can open in a new tab. The notifications inbox is
          not here: the bell in every shell opens it. */}
      <DropdownMenuGroup>
        <DropdownMenuItem render={<Link to="/settings" />}>
          <SettingsIcon
            aria-hidden="true"
            className="size-4"
            strokeWidth={1.5}
          />
          Settings
        </DropdownMenuItem>
        {adminEntry ? (
          // `render`, so the item *is* the link: a menu entry that navigates
          // from an onSelect handler cannot be opened in a new tab and has no
          // address to copy.
          <DropdownMenuItem render={<Link to={adminEntry.to} />}>
            <adminEntry.icon
              aria-hidden="true"
              className="size-4"
              strokeWidth={1.5}
            />
            {adminEntry.label}
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuGroup>
      <DropdownMenuSeparator />

      {/* One row, its choices side by side (spec 239), rather than a heading
          and three rows for something set once per device. Not a submenu: on
          a phone one opens over the menu it came from. Icons, so the row fits
          the sidebar's narrower menu; each still has its name, read out and
          shown on hover. */}
      <DropdownMenuGroup className="flex items-center gap-2 py-0.5 pr-1 pl-1.5">
        <span id={themeLabelId} className="flex-1 text-sm">
          Theme
        </span>
        <DropdownMenuRadioGroup
          aria-labelledby={themeLabelId}
          value={theme}
          onValueChange={(value) => {
            setTheme(value as Theme);
          }}
          className="bg-muted flex gap-0.5 rounded-md p-0.5"
        >
          {THEME_OPTIONS.map((option) => {
            const Icon = THEME_ICONS[option.value];
            return (
              <DropdownMenuRadioItem
                key={option.value}
                value={option.value}
                title={option.label}
                className="touch-target text-muted-foreground data-checked:bg-background data-checked:text-foreground size-7 justify-center p-0 data-checked:shadow-rest [&>[data-slot=dropdown-menu-radio-item-indicator]]:hidden"
              >
                <Icon aria-hidden="true" className="size-4" strokeWidth={1.5} />
                <span className="sr-only">{option.label}</span>
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuGroup>
      {phone ? null : (
        <DropdownMenuItem
          onClick={() => {
            openShortcuts(true);
          }}
        >
          Keyboard shortcuts
        </DropdownMenuItem>
      )}
      <DropdownMenuSeparator />
      {/* Signed in as the wrong person, the way to the right one, back on this
          same page (spec 193 US4, spec 196 US2). Another person follows, so
          this person's drafts go, as on signing out (spec 197). */}
      {switchAccount ? (
        <DropdownMenuItem
          onClick={() => {
            clearPersonalDrafts();
            switchAccount();
          }}
        >
          Switch account
        </DropdownMenuItem>
      ) : null}
      {/* This browser's copies of unsaved work go with the person: the next
          one to sign in here must not be offered them (specs 144, 197). */}
      <DropdownMenuItem
        onClick={() => {
          clearPersonalDrafts();
          signOut();
        }}
      >
        Sign out
      </DropdownMenuItem>
    </>
  );
}
